import { useQuery } from '@tanstack/react-query'
import { History, RotateCcw } from 'lucide-react'
import { useState } from 'react'

import { consulta, pedir } from '../api/nucleo'
import type { Lista } from '../api/tipos'
import { Aviso, Boton, Esqueleto, EstadoVacio, Modal, Pastilla, Tarjeta } from '../componentes/ui'
import { CABECERA_EDITADO_DESDE } from './conflicto'

/**
 * El historial de un documento con borradores (páginas, entradas, productos): las
 * versiones que guarda Payload —hasta 25 por documento— con su fecha, quién la guardó y
 * si se publicó. Se leen con los permisos de quien mira (un autor ve solo el historial de
 * lo suyo, §7).
 *
 * «Restaurar» usa la restauración de Payload **como borrador**: se guarda una versión
 * nueva con el contenido de la vieja, pasando por todos los hooks del núcleo y con los
 * permisos del usuario, y **no se publica nada**. La historia no se reescribe: lo de ahora
 * sigue ahí, como la versión anterior a la restaurada.
 */
type Version = { id: number | string; updatedAt: string; latest?: boolean; version: Record<string, unknown> & { _status?: string; modificadoPor?: null | string } }

export const fechaLarga = (iso: unknown): string =>
  typeof iso === 'string' && iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

export const Historial = ({
  coleccion,
  id,
  titulo,
  editadoDesde,
  sucio,
  alRestaurar,
  alFallar,
}: {
  coleccion: string
  id: number
  /** El campo título de la colección, para enseñar cómo se llamaba en cada versión. */
  titulo: string
  /** La marca de la versión abierta en el editor: restaurar encima de algo que otro acaba de guardar también es un conflicto (§18). */
  editadoDesde: string | undefined
  sucio: boolean
  alRestaurar: (fecha: string) => void
  alFallar: (e: unknown) => void
}) => {
  const [elegida, setElegida] = useState<null | Version>(null)
  const [restaurando, setRestaurando] = useState(false)
  const { data, isPending, error } = useQuery({
    queryKey: ['historial', coleccion, id],
    queryFn: () => pedir<Lista<Version>>(`/${coleccion}/versions${consulta({ 'where[parent][equals]': id, sort: '-updatedAt', limit: 25, depth: 0 })}`),
  })

  const restaurar = async () => {
    if (!elegida) return
    setRestaurando(true)
    try {
      await pedir(`/${coleccion}/versions/${elegida.id}${consulta({ draft: true, depth: 0 })}`, {
        metodo: 'POST',
        cuerpo: {},
        cabeceras: editadoDesde ? { [CABECERA_EDITADO_DESDE]: editadoDesde } : undefined,
      })
      const fecha = fechaLarga(elegida.updatedAt)
      setElegida(null)
      alRestaurar(fecha)
    } catch (e) {
      setElegida(null)
      alFallar(e)
    } finally {
      setRestaurando(false)
    }
  }

  if (isPending) return <Esqueleto filas={6} />
  if (error) return <Aviso tono="error">No se ha podido leer el historial. Vuelve a intentarlo.</Aviso>
  const versiones = data?.docs ?? []
  if (versiones.length === 0) return <EstadoVacio descripcion="Cada vez que guardes o publiques quedará aquí una versión, con la fecha y quién la guardó." icono={<History />} titulo="Todavía no hay historial" />

  return (
    <>
      <Tarjeta sinRelleno>
        <p className="border-b border-linea px-5 py-3 text-sm text-tenue">
          Las últimas {versiones.length === 1 ? 'versión' : `${versiones.length} versiones`}, de la más reciente a la más antigua. Restaurar una la guarda como borrador: tu web no cambia hasta que publiques.
        </p>
        <ol>
          {versiones.map((v, i) => {
            const nombre = v.version[titulo]
            const actual = i === 0
            return (
              <li className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-linea px-5 py-3 last:border-b-0" key={String(v.id)}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {fechaLarga(v.updatedAt)}
                    {actual && <span className="ml-2 text-xs font-normal text-tenue">(lo que tienes ahora)</span>}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-tenue">
                    {v.version.modificadoPor ? `Guardó ${v.version.modificadoPor}` : 'Sin autor apuntado'}
                    {typeof nombre === 'string' && nombre ? ` · «${nombre}»` : ''}
                  </p>
                </div>
                <Pastilla clave={v.version._status === 'published' ? 'publicado' : 'sin-publicar'}>{v.version._status === 'published' ? 'Publicada' : 'Borrador'}</Pastilla>
                {!actual && (
                  <Boton icono={<RotateCcw />} onClick={() => setElegida(v)} pequeno>
                    Restaurar
                  </Boton>
                )}
              </li>
            )
          })}
        </ol>
      </Tarjeta>

      {elegida && (
        <Modal
          ancho="max-w-md"
          cerrar={() => !restaurando && setElegida(null)}
          pie={
            <>
              <Boton disabled={restaurando} onClick={() => setElegida(null)} tipo="discreto">
                Cancelar
              </Boton>
              <Boton cargando={restaurando} onClick={() => void restaurar()} tipo="primario">
                Restaurar como borrador
              </Boton>
            </>
          }
          titulo={`Restaurar la versión del ${fechaLarga(elegida.updatedAt)}`}
        >
          <div className="flex flex-col gap-3 text-sm text-tinta-2">
            <p>Lo que ves en el editor se sustituye por lo que había entonces y se guarda como borrador. Lo publicado no cambia hasta que pulses «Publicar».</p>
            <p>Lo de ahora no se pierde: queda en este historial y lo puedes volver a restaurar.</p>
            {sucio && <Aviso tono="aviso">Tienes cambios sin guardar en el editor: si restauras, se pierden.</Aviso>}
          </div>
        </Modal>
      )}
    </>
  )
}
